import { useQuery } from '@tanstack/react-query';

import type { CustomBulletinBoard } from '@autopass-web/types';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { z, ZodTypeAny } from 'zod';

const USER_API_V3_ENDPOINT_ALPHA = 'https://api-alpha.pklotcorp.com/v3';
const USER_API_V3_ENDPOINT_PROD = 'https://api.autopass.xyz/v3';

export type BulletinAttr = {
  [key: string]: string | undefined;
  sub_title?: string;
  image_url?: string;
  background_color?: string;
  parkinglot_ids?: string;
  title?: string;
};

export type Board<TAttr> = CustomBulletinBoard<TAttr & BulletinAttr>;

type BoardsData<TKey extends string, TAttr> = {
  data: {
    [key in TKey]: Board<TAttr>[];
  };
};

type Response<TKey extends string, TAttr> = AxiosResponse<
  BoardsData<TKey, TAttr>
>;

export const useBulletin = <
  TBarcode extends string,
  TAttrSchema extends ZodTypeAny = ZodTypeAny,
  TAttr = z.infer<TAttrSchema>,
>({
  boardCode,
  partnerId,
  distributor,
  clientInstance,
  options = {},
  attributesSchema,
}: {
  boardCode: TBarcode;
  partnerId?: string;
  distributor?: string;
  clientInstance: AxiosInstance;
  options?: Omit<
    UseQueryOptions<Response<TBarcode, TAttr>, Error, Board<TAttr>[]>,
    'queryKey' | 'queryFn' | 'select'
  >;
  attributesSchema: TAttrSchema;
}) => {
  const baseUrl = clientInstance?.defaults?.baseURL;
  const isAlphaEnv = baseUrl ? !!baseUrl.match('alpha') : false;

  const queryFn = () => {
    const userAPIv3Client = clientInstance.create({
      baseURL: isAlphaEnv
        ? USER_API_V3_ENDPOINT_ALPHA
        : USER_API_V3_ENDPOINT_PROD,
    });

    return userAPIv3Client.get<BoardsData<TBarcode, TAttr>>(
      '/bulletin_boards',
      {
        params: { codes: boardCode, partner_id: partnerId, distributor },
      },
    );
  };

  const query = useQuery<Response<TBarcode, TAttr>, Error, Board<TAttr>[]>({
    ...options,
    queryKey: ['bulletin_boards', boardCode] as const,
    queryFn,
    select: (response) => {
      const availBoards = (response.data?.data?.[boardCode] ?? []).filter(
        (b) => {
          const { success } = attributesSchema.safeParse(b.attributes);

          return success;
        },
      );

      return availBoards;
    },
  });

  return { ...query, isAlphaEnv };
};
